import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Button,
  CircularProgress,
} from "@mui/material";
import { CheckCircle, Cancel } from "@mui/icons-material";
import Color from "../../theme/color";

const steps = [
  { title: "Checking Liveness", desc: "Verifying photo authenticity" },
  { title: "Verifying Document", desc: "Fetching and validating ID" },
  { title: "Matching Face", desc: "Selfie vs ID photo" },
  { title: "Matching Name", desc: "Booking vs ID name" },
  { title: "Issuing Credential", desc: "Creating secure credential" },
  { title: "Storing Securely", desc: "Encrypted and saved safely" },
];

export default function VerificationPopup({
  open = true,
  onClose,
  startStep = 0,
  stopStep = steps.length,
  errorStep = null, // 👈 pass error step index (e.g., 3 for "Name Match")
  errorMsg = "",
}) {
  const [activeStep, setActiveStep] = useState(startStep);
  const [completed, setCompleted] = useState({});

  useEffect(() => {
    if (open) {
      setActiveStep(startStep);
      setCompleted({});
    }
  }, [open, startStep]);

  useEffect(() => {
    if (!open) return;

    if (activeStep < stopStep) {
      const timer = setTimeout(() => {
        // ✅ Handle error step properly
        if (errorStep !== null && activeStep === errorStep) {
          setCompleted((prev) => ({ ...prev, [activeStep]: false })); // mark error
          return; // stop here, don't increment further
        }

        // ✅ Handle normal step (success)
        setCompleted((prev) => ({ ...prev, [activeStep]: true }));
        setActiveStep((prev) => prev + 1);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [open, activeStep, stopStep, errorStep]);

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 20,
          overflow: "hidden",
          backgroundColor: "white",
        },
      }}
    >
      {/* Top Shield Icon */}
      <Box display="flex" justifyContent="center" mt={3}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "16px",
            background: Color.digipass,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span role="img" aria-label="shield" style={{ fontSize: 28 }}>
            🛡️
          </span>
        </Box>
      </Box>

      {/* Title */}
      <Typography
        variant="h6"
        align="center"
        fontWeight="bold"
        sx={{
          mt: 2,
          mb: 1,
          color:
            activeStep !== stopStep
              ? "black"
              : errorStep !== null
              ? "red"
              : "green",
        }}
      >
        {activeStep !== stopStep
          ? "Verifying"
          : errorStep !== null
          ? "Verification Unsuccessful"
          : "Verification Successful"}
      </Typography>

      {/* Progress Counter */}
      <Typography
        variant="body2"
        align="center"
        sx={{
          background: "#EEF2FF",
          color: "#0d0c28ff",
          borderRadius: "12px",
          px: 2,
          py: 0.5,
          fontWeight: 600,
          width: "fit-content",
          mx: "auto",
        }}
      >
        {activeStep}/{stopStep} Complete
      </Typography>

      {/* Steps */}
      <DialogContent sx={{ mt: 0, pb: 2, pt: 0.5 }}>
        {steps.map((step, index) => {
          const isError = errorStep === index;
          const isCompleted = completed[index];
          const isActive = index === activeStep && !isError;

          return (
            <Box
              key={step.title}
              position="relative"
              display="flex"
              alignItems="flex-start"
              mb={3}
            >
              {/* Connector Line */}
              {index < steps.length - 1 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 28,
                    left: 15,
                    width: "2px",
                    height: "calc(100% - 28px)",
                    backgroundColor: "#e0e0e0",
                    zIndex: 0,
                  }}
                />
              )}

              {/* Icon */}
              <Box
                mr={2}
                zIndex={1}
                sx={{ display: "flex", alignItems: "center" }}
              >
                {completed[index] === true ? (
                  <CheckCircle sx={{ color: "green" }} />
                ) : completed[index] === false ? (
                  <Cancel sx={{ color: "red" }} />
                ) : activeStep === index ? (
                  <CircularProgress
                    size={22}
                    thickness={5}
                    sx={{ color: "#0c0b24ff" }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      border: "2px solid #ccc",
                    }}
                  />
                )}
              </Box>

              {/* Text */}
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={
                    completed[index] === true
                      ? "green"
                      : completed[index] === false
                      ? "red"
                      : activeStep === index
                      ? "#0f0d2cff"
                      : "#333"
                  }
                >
                  {step.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={
                    completed[index] === true
                      ? "green"
                      : completed[index] === false
                      ? "red"
                      : activeStep === index
                      ? "#1d1a55ff"
                      : "#333"
                  }
                >
                  {isError ? `Verification failed ( ${errorMsg} )` : step.desc}
                </Typography>
              </Box>
            </Box>
          );
        })}

        {/* Next Step Button */}
        {
          <Button
            variant="contained"
            fullWidth
            sx={{
              mt: 1,
              background:
                errorStep !== null
                  ? "linear-gradient(90deg, #ef4444, #b91c1c)"
                  : Color.digipass,
              borderRadius: "12px",
              fontWeight: "bold",
              textTransform: "none",
              py: 1.2,
              fontSize: "1rem",
            }}
            onClick={onClose}
            disabled={!(activeStep === stopStep || errorStep !== null)}
          >
            {errorStep !== null ? "Retry" : "OK"}
          </Button>
        }
      </DialogContent>
    </Dialog>
  );
}
